// Cosmetics "Farbe" (Design §11.2): multi-coloured hull paint or a national flag. It replaces the
// player colour on hull and tracks, the turret keeps it; a Lackierung is laid over it.
import { FLAGS, drawFlag } from './flags.js';

/** Item ids (`server::looks`, kind COLOR): the patterns, then the flags in `FLAGS` order. */
export const PATTERN_BASE = 57;
export const FLAG_BASE = 71;

/** Gradient colour stops per pattern, top-left to bottom-right of the hull; `move`: drifts with time. */
const PATTERNS: { name: string; stops: string[]; move?: number; draw?: (ctx: CanvasRenderingContext2D, s: number, now: number) => void }[] = [
  { name: 'Rainbow', stops: ['#e53935', '#fb8c00', '#fdd835', '#43a047', '#1e88e5', '#8e24aa'] },
  { name: 'Lava', stops: ['#1a0500', '#b71c1c', '#ff6f00', '#ffca28', '#b71c1c', '#1a0500'], move: 1 },
  { name: 'Sunset', stops: ['#2c1e5c', '#8e2a73', '#e8505b', '#f9a03f', '#fbe38e'] },
  { name: 'Strawberry', stops: ['#e0174a', '#ff3d6b', '#e0174a'], draw: seeds },
  { name: 'Donut', stops: ['#c98a4b', '#e2a865', '#c98a4b'], draw: sprinkles },
  { name: 'Electric Blue', stops: ['#001b4d', '#0066ff', '#36e1ff', '#0066ff', '#001b4d'], move: 3, draw: sparks },
  { name: 'Sky', stops: ['#4fa3e3', '#8ccbf5', '#d8efff'], draw: clouds },
  { name: 'Ocean', stops: ['#03254c', '#1167b1', '#2a9df4', '#d0efff'], move: 0.5 },
  { name: 'Aurora', stops: ['#06142e', '#1de9b6', '#2979ff', '#d500f9', '#06142e'], move: 0.7 },
  { name: 'Galaxy', stops: ['#0b0322', '#3a0ca3', '#7209b7', '#0b0322'], draw: stars },
  { name: 'Cotton candy', stops: ['#ffb3d9', '#c9b6ff', '#a0e7ff', '#ffb3d9'] },
  { name: 'Gold', stops: ['#7a5a12', '#e8c66a', '#fff4c2', '#e8c66a', '#7a5a12'], move: 0.4 },
  { name: 'Leopard', stops: ['#c48a35', '#e3b768', '#c48a35'], draw: rosettes },
  { name: 'Watermelon', stops: ['#1b7a2c', '#4caf50', '#ff5a70', '#ff5a70', '#ff5a70'], draw: melonSeeds },
];

export const PATTERN_NAMES = PATTERNS.map((p) => p.name);

/** Whether `id` is a COLOR item: a pattern or a flag. */
export function isColor(id: number): boolean {
  return colorOf(id) !== undefined;
}

function colorOf(id: number) {
  if (id >= FLAG_BASE && id < FLAG_BASE + FLAGS.length) return { flag: FLAGS[id - FLAG_BASE] };
  const p = PATTERNS[id - PATTERN_BASE];
  return id >= PATTERN_BASE && p ? { pattern: p } : undefined;
}

/**
 * Fills the square −s..s around the origin with colour `id` at `now` (ms); the caller clips to
 * the hull and tracks, or to a swatch. Flags lie with the hoist at −x (the tank's rear).
 */
export function drawColor(ctx: CanvasRenderingContext2D, id: number, s: number, now: number): void {
  const c = colorOf(id);
  if (!c) return;
  ctx.save();
  if (c.flag) {
    ctx.translate(-s, -s * 0.85);
    drawFlag(ctx, c.flag, 2 * s, 1.7 * s);
    ctx.restore();
    return;
  }
  const p = c.pattern!;
  const shift = p.move ? ((now / 1000) * p.move * 0.3) % 1 : 0;
  const g = ctx.createLinearGradient(-s, -s, s, s);
  const n = p.stops.length;
  // Moving patterns run the stops round (the first and last are alike for them).
  p.stops.forEach((col, k) => g.addColorStop((k / (n - 1) + shift) % 1, col));
  if (shift) g.addColorStop(0, p.stops[0]);
  ctx.fillStyle = g;
  ctx.fillRect(-s, -s, 2 * s, 2 * s);
  p.draw?.(ctx, s, now);
  ctx.restore();
}

/** A small square of colour `id` (for player lists), `size` CSS px. */
export function colorSwatch(id: number, size = 11): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  const dpr = window.devicePixelRatio || 1;
  canvas.width = canvas.height = Math.round(size * dpr);
  canvas.style.width = canvas.style.height = `${size}px`;
  canvas.className = 'swatch';
  const ctx = canvas.getContext('2d')!;
  const s = canvas.width / 2;
  ctx.translate(s, s);
  drawColor(ctx, id, s, 0);
  return canvas;
}

const rand = (k: number) => {
  const x = Math.sin(k * 12.9898) * 43758.5453;
  return x - Math.floor(x);
};

function seeds(ctx: CanvasRenderingContext2D, s: number): void {
  ctx.fillStyle = '#ffe680';
  for (let k = 0; k < 26; k++) {
    ctx.beginPath();
    ctx.ellipse((rand(k) * 2 - 1) * s, (rand(k + 50) * 2 - 1) * s, s * 0.035, s * 0.06, rand(k + 9), 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.fillStyle = '#2e7d32';
  ctx.fillRect(s * 0.75, -s, s * 0.25, 2 * s); // the green top at the front
}

function sprinkles(ctx: CanvasRenderingContext2D, s: number): void {
  ctx.fillStyle = '#ff8fc8';
  ctx.beginPath();
  ctx.arc(0, 0, s * 0.82, 0, Math.PI * 2);
  ctx.fill();
  const colors = ['#ffffff', '#ffeb3b', '#4fc3f7', '#81c784', '#e57373'];
  ctx.lineWidth = s * 0.06;
  ctx.lineCap = 'round';
  for (let k = 0; k < 24; k++) {
    const [x, y, a] = [(rand(k) * 2 - 1) * s * 0.7, (rand(k + 30) * 2 - 1) * s * 0.7, rand(k + 60) * Math.PI];
    ctx.strokeStyle = colors[k % colors.length];
    ctx.beginPath();
    ctx.moveTo(x - Math.cos(a) * s * 0.07, y - Math.sin(a) * s * 0.07);
    ctx.lineTo(x + Math.cos(a) * s * 0.07, y + Math.sin(a) * s * 0.07);
    ctx.stroke();
  }
  ctx.fillStyle = '#c98a4b'; // the hole
  ctx.beginPath();
  ctx.arc(0, 0, s * 0.18, 0, Math.PI * 2);
  ctx.fill();
}

function sparks(ctx: CanvasRenderingContext2D, s: number, now: number): void {
  ctx.strokeStyle = '#e0ffff';
  ctx.lineWidth = s * 0.04;
  const k0 = Math.floor(now / 120);
  for (let k = k0; k < k0 + 3; k++) {
    let [x, y] = [(rand(k) * 2 - 1) * s, -s];
    ctx.beginPath();
    ctx.moveTo(x, y);
    while (y < s) {
      x += (rand(x + y) - 0.5) * s * 0.4;
      y += s * 0.25;
      ctx.lineTo(x, y);
    }
    ctx.stroke();
  }
}

function clouds(ctx: CanvasRenderingContext2D, s: number): void {
  ctx.fillStyle = 'rgba(255, 255, 255, 0.85)';
  for (const [cx, cy, r] of [[-0.5, -0.4, 0.22], [-0.3, -0.45, 0.28], [-0.1, -0.4, 0.2], [0.4, 0.35, 0.22], [0.6, 0.3, 0.26], [0.75, 0.38, 0.18]]) {
    ctx.beginPath();
    ctx.arc(cx * s, cy * s, r * s, 0, Math.PI * 2);
    ctx.fill();
  }
}

function stars(ctx: CanvasRenderingContext2D, s: number, now: number): void {
  for (let k = 0; k < 40; k++) {
    ctx.globalAlpha = 0.5 + 0.5 * Math.sin(now / 400 + k * 1.7);
    ctx.fillStyle = k % 7 ? '#ffffff' : '#ffd6ff';
    ctx.fillRect((rand(k) * 2 - 1) * s, (rand(k + 80) * 2 - 1) * s, s * 0.04, s * 0.04);
  }
  ctx.globalAlpha = 1;
}

/** Leopard: broken dark rings round a deeper tan, small black spots between. */
function rosettes(ctx: CanvasRenderingContext2D, s: number): void {
  for (let k = 0; k < 16; k++) {
    const [x, y, r] = [(rand(k) * 2 - 1) * s, (rand(k + 40) * 2 - 1) * s, s * (0.1 + 0.05 * rand(k + 70))];
    ctx.fillStyle = '#a8681f';
    ctx.beginPath();
    ctx.arc(x, y, r * 0.8, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = '#2a1a0c';
    ctx.lineWidth = s * 0.05;
    ctx.lineCap = 'round';
    for (let arc = 0; arc < 3; arc++) {
      const a0 = rand(k + arc * 11) * 0.6 + (arc * Math.PI * 2) / 3;
      ctx.beginPath();
      ctx.arc(x, y, r, a0, a0 + 1.4);
      ctx.stroke();
    }
  }
  ctx.fillStyle = '#2a1a0c';
  for (let k = 0; k < 14; k++) {
    ctx.beginPath();
    ctx.arc((rand(k + 100) * 2 - 1) * s, (rand(k + 130) * 2 - 1) * s, s * 0.03, 0, Math.PI * 2);
    ctx.fill();
  }
}

function melonSeeds(ctx: CanvasRenderingContext2D, s: number): void {
  ctx.fillStyle = '#1b1b1b';
  for (let k = 0; k < 14; k++) {
    ctx.beginPath();
    ctx.ellipse((0.1 + rand(k) * 0.85) * s, (rand(k + 40) * 2 - 1) * s * 0.85, s * 0.035, s * 0.06, 0.3, 0, Math.PI * 2);
    ctx.fill();
  }
}

/** Marks a player in lists: the square of its colour look, else the dot in its player colour. */
export function playerMark(color: string, look: number): HTMLElement {
  // Both in a box of the same width, so the names behind them line up.
  const mark = document.createElement('span');
  mark.className = 'mark';
  if (isColor(look)) mark.append(colorSwatch(look));
  else {
    mark.textContent = '●';
    mark.style.color = color;
  }
  return mark;
}

const TILE_PX = 96; // a colour look for the turret, drawn at this size
const tiles = new Map<number, { canvas: HTMLCanvasElement; at: number }>();

/**
 * Colour look `id` as a fill for the turret, lined up with `drawColor(…, s, now)` on the hull, so
 * the turret carries the piece under it; `undefined` for no colour look. Flags and still patterns
 * are drawn once, moving ones anew each frame.
 */
export function turretFill(ctx: CanvasRenderingContext2D, id: number, s: number, now: number): CanvasPattern | undefined {
  const c = colorOf(id);
  if (!c) return undefined;
  const moving = !!c.pattern && (!!c.pattern.move || !!c.pattern.draw);
  let tile = tiles.get(id);
  if (!tile || (moving && tile.at !== now)) {
    const canvas = tile?.canvas ?? document.createElement('canvas');
    canvas.width = canvas.height = TILE_PX;
    const t = canvas.getContext('2d')!;
    t.translate(TILE_PX / 2, TILE_PX / 2);
    drawColor(t, id, TILE_PX / 2, now);
    tile = { canvas, at: now };
    tiles.set(id, tile);
  }
  const pattern = ctx.createPattern(tile.canvas, 'no-repeat');
  pattern?.setTransform(new DOMMatrix().translate(-s, -s).scale((2 * s) / TILE_PX));
  return pattern ?? undefined;
}
